import { useCallback, useState } from 'react';
import { Platform } from 'react-native';
import { useHoverState } from '../../../hooks/useHoverState';
import type { CalendarEvent } from '../../../types/calendar';

interface EventPreviewTriggerProps {
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onLongPress?: () => void;
  delayLongPress?: number;
  onPressOut?: () => void;
}

interface UseEventPreviewReturn {
  previewEvent: CalendarEvent | null;
  triggerProps: EventPreviewTriggerProps;
  dismiss: () => void;
}

const LONG_PRESS_DELAY_MS = 350;

// The platform seam: EventPreview itself is one shared, presentational
// component (EventPreview.tsx) with zero trigger logic — this hook is the
// only place that decides HOW the preview opens per platform. Web reuses
// useHoverState().hoverProps directly (onMouseEnter/onMouseLeave); native
// gets its own long-press trigger and never binds onHoverIn — it does not
// fire on a touch tap, so a hover-only implementation would silently
// dead-end on mobile, which is the whole reason this hook exists instead of
// spreading hoverProps everywhere unconditionally. `onPress` is
// deliberately never part of triggerProps, on either platform, so a
// long-press preview never consumes the tap that opens the event.
export function useEventPreview(event: CalendarEvent): UseEventPreviewReturn {
  const { isHovered, hoverProps, setIsHovered } = useHoverState();
  const [isLongPressed, setIsLongPressed] = useState(false);

  const showOnLongPress = useCallback(() => setIsLongPressed(true), []);
  const dismissLongPress = useCallback(() => setIsLongPressed(false), []);

  const isWeb = Platform.OS === 'web';
  const isPreviewing = isWeb ? isHovered : isLongPressed;

  // Platform.OS checked directly, not Platform.select: the latter is not
  // reliably re-evaluated when a test mutates Platform.OS at runtime under
  // this project's jest-expo/web setup, which is why every other
  // platform-branching call site in the app (ChatInput.tsx and friends)
  // already uses this same direct-comparison form.
  const triggerProps: EventPreviewTriggerProps = isWeb
    ? hoverProps
    : {
        onLongPress: showOnLongPress,
        delayLongPress: LONG_PRESS_DELAY_MS,
        onPressOut: dismissLongPress,
      };

  const dismiss = useCallback(() => {
    setIsHovered(false);
    setIsLongPressed(false);
  }, [setIsHovered]);

  return {
    previewEvent: isPreviewing ? event : null,
    triggerProps,
    dismiss,
  };
}
